import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { PRODUCTS_BY_ID } from '../../constants/products';
import {
  fetchUserSubscriptions,
  type ActiveSubscription,
  type InactiveSubscription,
  type UserSubscriptionResponse,
} from '../../services/userManager';

export type UserSubscriptionSummary = {
  subscriptionId: number | null;
  productId: number;
  name: string;
  image: string | null;
  category: string | null;
  untilDate: string | null;
  isActive: boolean;
};

export type UserSubscriptionsResult = {
  active: ActiveSubscription[];
  inactive: InactiveSubscription[];
  allProducts: UserSubscriptionSummary[];
  activeProductIds: number[];
};

export const userSubscriptionsQueryKey = ['user', 'subscriptions'];

const mapSubscriptions = (
  payload: UserSubscriptionResponse
): UserSubscriptionsResult => {
  const active = payload.active ?? [];
  const inactive = payload.unactive ?? [];

  const summaries: UserSubscriptionSummary[] = [
    ...active.map(subscription => ({
      subscriptionId: subscription.user_product_id,
      productId: subscription.product_id,
      name:
        PRODUCTS_BY_ID[subscription.product_id]?.name ??
        subscription.name ??
        '',
      image: subscription.image ?? null,
      category:
        PRODUCTS_BY_ID[subscription.product_id]?.category ??
        subscription.category ??
        null,
      untilDate: subscription.until_date ?? null,
      isActive: true,
    })),
    ...inactive.map(subscription => ({
      subscriptionId: null,
      productId: subscription.product_id,
      name:
        PRODUCTS_BY_ID[subscription.product_id]?.name ??
        subscription.name ??
        '',
      image: subscription.image ?? null,
      category:
        PRODUCTS_BY_ID[subscription.product_id]?.category ??
        subscription.category ??
        null,
      untilDate: null,
      isActive: false,
    })),
  ];

  return {
    active,
    inactive,
    allProducts: summaries,
    activeProductIds: active.map(subscription => subscription.product_id),
  };
};

export function useUserSubscriptionsQuery(
  options?: UseQueryOptions<UserSubscriptionsResult, Error>
) {
  return useQuery<UserSubscriptionsResult, Error>({
    queryKey: userSubscriptionsQueryKey,
    queryFn: async () => {
      const payload = await fetchUserSubscriptions();
      return mapSubscriptions(payload);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}
